import React from 'react';
import { Alert, Button, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useYield } from './YieldContext';
import './yield.css';

export function stageLabel(stage, t) {
  return stage === 'final' ? t('yield.final') : t('yield.day', { day: stage.replace('das', '') });
}
const format = (value, locale, digits = 1) => value == null || value === '' ? '—'
  : new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(Number(value));

export function YieldLegend() {
  const y = useYield(); const { t, i18n } = useTranslation('translation');
  if (!y?.active || !y.dataset) return null;
  return <div className="cc-legend" aria-label={t('yield.legend')}>
    <strong>{t('yield.title')} · {y.year} · {stageLabel(y.stage, t)} · kg/ha</strong>
    <div className="cc-legend-items">{y.catalog.data.bins.map(bin => <span key={bin.min}>
      <i style={{ backgroundColor: bin.color }} />{bin.max == null ? `≥ ${format(bin.min, i18n.language, 0)}`
        : `${format(bin.min, i18n.language, 0)}–<${format(bin.max, i18n.language, 0)}`}</span>)}
      <span><i style={{ backgroundColor: '#bdbdbd' }} />{t('yield.noData')}</span>
    </div>
  </div>;
}

function Progression({ stages, t, locale }) {
  const valid = stages.filter(s => s.value != null && Number.isFinite(Number(s.value)));
  const max = Math.max(14400, ...valid.map(s => Number(s.value)));
  return <div className="yield-progression">
    <svg viewBox="0 0 310 145" role="img" aria-label={t('yield.progression')}>
      {[0, .5, 1].map(ratio => <g key={ratio}><line x1="38" x2="300" y1={110 - ratio * 90} y2={110 - ratio * 90} stroke="#ddd" />
        <text x="34" y={114 - ratio * 90} textAnchor="end" fontSize="9">{format(max * ratio, locale, 0)}</text></g>)}
      {stages.map((s, index) => s.value == null ? null : <g key={s.stage}>
        <circle cx={48 + index * 48} cy={110 - Number(s.value) / max * 90} r="4" fill="#26834a">
          <title>{stageLabel(s.stage, t)}: {format(s.value, locale)} kg/ha</title>
        </circle>
        {index > 0 && stages[index - 1].value != null && <line x1={48 + (index - 1) * 48}
          y1={110 - Number(stages[index - 1].value) / max * 90} x2={48 + index * 48}
          y2={110 - Number(s.value) / max * 90} stroke="#26834a" strokeWidth="2" />}
      </g>)}
      {stages.map((s, index) => <text key={s.stage} x={48 + index * 48} y="134" textAnchor="middle" fontSize="9">{stageLabel(s.stage, t)}</text>)}
    </svg>
    <table className="yield-table"><thead><tr><th>{t('yield.stage')}</th><th>kg/ha</th><th>{t('yield.uncertainty')}</th></tr></thead>
      <tbody>{stages.map(s => <tr key={s.stage}><td>{stageLabel(s.stage, t)}</td><td>{format(s.value, locale)}</td>
        <td>{s.uncertainty == null ? '—' : `± ${format(s.uncertainty, locale)}`}</td></tr>)}</tbody></table>
  </div>;
}

export default function YieldPanel() {
  const y = useYield(); const { t, i18n } = useTranslation('translation');
  const f = v => format(v, i18n.language);
  const data = y.summary.data; const stats = data?.summary; const field = y.selected?.properties;
  const stages = (data?.stages || []).map(s => ({ stage: s.stage,
    value: field ? field[s.stage === 'final' ? 'expected_yield' : `yield_${s.stage}`] : s.mean_yield_kg_ha,
    uncertainty: field ? field[`unc_${s.stage}`] : s.stage_uncertainty_kg_ha }));
  return <div className="ci-dashboard yield-dashboard">
    <div className="ci-panel-controls">
      <h3>{t('yield.location')}</h3>
      <p className="ci-muted">{t('yield.prototype')}</p>
      <div className="yield-controls">
        <label><span className="ci-label">{t('yield.year')}</span><Select aria-label={t('yield.year')} value={y.year}
          options={(y.dataset?.years || [{ year: 2024 }, { year: 2025 }]).map(r => ({ value: r.year, label: String(r.year) }))}
          onChange={y.setYear} /></label>
        <label><span className="ci-label">{t('yield.stage')}</span><Select aria-label={t('yield.stage')} value={y.stage}
          options={(y.catalog.data?.stages || ['das30','das45','das60','das75','das90','final']).map(stage => ({ value: stage, label: stageLabel(stage, t) }))}
          onChange={y.setStage} /></label>
      </div>
    </div>
    <div className="ci-panel-results">
      {[y.catalog.status, y.summary.status].includes('loading') && <p role="status">{t('yield.loading')}</p>}
      {[y.catalog.status, y.summary.status].includes('error') && <Alert type="error" message={t('yield.error')}
        action={<Button onClick={y.retry}>{t('cc.retry')}</Button>} />}
      {y.catalog.status === 'ready' && !y.yearInfo && <p>{t('yield.unavailable')}</p>}
      {stats && <>
        <div className="yield-metrics">
          <div><span>{t('yield.average')}</span><strong>{f(stats.mean_yield_kg_ha)} <small>kg/ha</small></strong></div>
          <div><span>{t('yield.fields')}</span><strong>{f(stats.field_count)}</strong></div>
          <div><span>{t('yield.area')}</span><strong>{f(stats.area_ha)} <small>ha</small></strong></div>
          <div><span>{t('yield.coverage')}</span><strong>{stats.area_ha ? f(100 * stats.valid_area_ha / stats.area_ha) : '—'}%</strong></div>
        </div>
        <p className="ci-muted">{t('yield.coverageHelp')}</p>
        {!!stats.few_scenes_count && <Alert type="warning" message={t('yield.fewScenes', { count: stats.few_scenes_count })} />}
        <h4>{t('yield.distribution')}</h4>
        <div className="yield-distribution">{data.distribution.map(row => {
          const bin = y.catalog.data.bins[row.bin]; const percent = stats.area_ha ? 100 * row.area_ha / stats.area_ha : 0;
          return <div key={row.bin}><span>{bin ? (bin.max ? `${f(bin.min)}–<${f(bin.max)}` : `≥ ${f(bin.min)}`) : t('yield.noData')}</span>
            <div className="yield-bar"><i style={{ width: `${percent}%`, background: bin?.color || '#bdbdbd' }} /></div>
            <span>{f(percent)}%</span></div>;
        })}</div>
        <section className="yield-field-card" aria-label={t('yield.fieldDetails')}>
          <h4>{field ? field.poly_id : t('yield.regionalProgression')}</h4>
          {field ? <><Button size="small" onClick={() => y.select(null)}>{t('yield.clearField')}</Button>
            <dl className="yield-details">
              <dt>{t('yield.area')}</dt><dd>{f(field.area_ha)} ha</dd>
              <dt>{t('yield.observations')}</dt><dd>{field.n_obs}</dd>
              <dt>{t('yield.confidence')}</dt><dd>{f(field.class_conf * 100)}%</dd>
              <dt>{t('yield.quality')}</dt><dd>{t(`yield.flag_${field.data_flag}`)}</dd>
              <dt>{t('yield.sowing')}</dt><dd>{field.sowing_date_region || '—'}</dd>
              <dt>{t('yield.greenup')}</dt><dd>{field.greenup_date || '—'}</dd>
              <dt>{t('yield.peak')}</dt><dd>{f(field.peak_ndvi)} · {field.peak_date || '—'}</dd>
            </dl></> : <p>{t('yield.clickField')}</p>}
          <Progression stages={stages} t={t} locale={i18n.language} />
        </section>
        <details className="yield-method"><summary>{t('yield.method')}</summary><p>{t('yield.methodHelp')}</p><p>{t('yield.uncertaintyHelp')}</p></details>
      </>}
    </div>
  </div>;
}
